import {formatDate, formatNumber} from '@angular/common';
import {Component, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, filter, map} from 'rxjs/operators';
import {IPlatformToolComponent, IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommandResponse} from '@shared/commands/command-base';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {countOf} from '@features/admin-panel/helpers/admin-dashboard-text';
import {
  ADB_DIRECTORY_DEFAULTS,
  adbDirectoryDefaultDir,
  AdminDashboardDirectory,
  AdminDashboardDirectoryKind,
  AdminDashboardDirectoryParams,
  AdminDashboardDirectoryRow,
  AdminDashboardDirectorySort,
  AdminDashboardDirectoryStatus,
  AdminDashboardDirectoryTierFilter
} from '@features/admin-panel/models/admin-dashboard-directory.models';

/** Tier → the words on its badge, as the Needs attention rows say them (OCA-E-159 M/attention.ts:27). */
const TIER_LABEL: {[tier: string]: string} = {critical: 'Critical', warning: 'Warning', watch: 'Watch', healthy: 'Healthy'};
/** Flag kind → its Font Awesome icon, as on the Needs attention chips (attention component FLAG_ICON). */
const FLAG_ICON: {[kind: string]: string} = {renewal: 'fa-file-contract', dark: 'fa-moon', usage: 'fa-arrow-trend-down', spike: 'fa-chart-line-up'};

/**
 * The Company 360 directory ('/admin-panel/company-360', Plan ITP-P-68 stories 13–18): every company with its health,
 * open flags, active users and last login, searched, filtered, sorted and paged on the server
 * (`AdminDashboardCompaniesQuery`, ITP-E-303). A row opens that company's Company 360. The search (ITP-E-304-04), the
 * filters (-05), the sort headers (-06) and the pager (-07) change `params` and call `load()`.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-companies',
  templateUrl: './admin-dashboard-companies.component.html',
  styleUrls: ['./admin-dashboard-companies.component.scss']
})
export class AdminDashboardCompaniesComponent implements IPlatformToolComponent, OnDestroy {

  handler: IPlatformToolHandler;
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  selectedTab: TabModel;

  /** What the directory asks the server for; it opens on ADB_DIRECTORY_DEFAULTS. */
  params: AdminDashboardDirectoryParams = {...ADB_DIRECTORY_DEFAULTS};
  /** The page on screen. */
  rows: AdminDashboardDirectoryRow[] = [];
  /** Every company that matches, over all pages. */
  total = 0;
  /** The page and page size the server served (it clamps the page size to 10..100). */
  page = ADB_DIRECTORY_DEFAULTS.page;
  pageSize = ADB_DIRECTORY_DEFAULTS.pageSize;
  /** The insight day the tiers and scores are from; null before the first nightly run. */
  asOf: string | null = null;
  /** A request is on its way. */
  loading = false;
  /** At least one response (or failure) arrived. */
  loaded = false;
  /** The last request failed. */
  failed = false;
  readonly failedTitle = 'Couldn\'t load the companies';

  private loadSubscription: Subscription;
  /** What the search box shows; `params.search` follows it, trimmed, 300 ms after the last keystroke (ITP-E-304-04). */
  searchText = '';
  private readonly searchInput = new Subject<string>();
  private readonly searchSubscription: Subscription = this.searchInput.pipe(
    debounceTime(300),
    map(text => text.trim()),
    filter(search => search !== this.params.search)
  ).subscribe(search => {
    this.params = {...this.params, search, page: 1};
    this.load();
  });

  constructor(private dataService: AdminDashboardDataService, private router: Router) {}

  /** "2,508 companies". */
  get totalText(): string {
    return countOf(this.total, 'company', 'companies');
  }

  /** "Health as of 14 Oct 2026": the insight day the tiers and scores are from; before the first nightly run, none. */
  get asOfText(): string {
    return this.asOf ? `Health as of ${formatDate(this.asOf, 'd MMM y', 'en-US', 'UTC')}` : 'No health scores yet';
  }

  onLoad(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.searchSubscription.unsubscribe();
  }

  /** Asks for `params`; a newer request cancels an older one, so a slow page never replaces a newer one. */
  load(): void {
    this.loadSubscription?.unsubscribe();
    this.loading = true;
    this.loadSubscription = this.dataService.getCompanies({...this.params}).subscribe({
      next: response => this.show(response, false),
      error: () => this.show(null, true)
    });
  }

  /** Opens the company's Company 360 (story 18); its breadcrumb leads back here (ITP-E-303, D-D6). */
  open(row: AdminDashboardDirectoryRow): void {
    this.router.navigateByUrl('/admin-panel/company-360/' + row.companyId);
  }

  /** "Critical", "Warning", "Watch" or "Healthy" for the tier badge. */
  tierLabel(tier: string): string {
    return TIER_LABEL[tier] ?? tier;
  }

  flagIcon(kind: string): string {
    return FLAG_ICON[kind] ?? 'fa-flag';
  }

  private show(response: AdminDashboardDirectory | null, failed: boolean): void {
    this.rows = Array.isArray(response?.Rows) ? response.Rows : [];
    this.total = typeof response?.Total === 'number' ? response.Total : this.rows.length;
    this.page = typeof response?.Page === 'number' ? response.Page : this.params.page;
    this.pageSize = typeof response?.PageSize === 'number' ? response.PageSize : this.params.pageSize;
    this.asOf = response?.AsOf ?? null;
    this.failed = failed;
    this.loading = false;
    this.loaded = true;
  }

  /** The search box changed: keep the text and let the 300 ms debounce ask the server (ITP-E-304-04). */
  onSearch(text: string): void {
    this.searchText = text;
    this.searchInput.next(text);
  }

  /** The filter choices (ITP-E-304-05, Plan ITP-P-68 D-D3); the values are the query's. */
  readonly tierOptions: ReadonlyArray<{value: AdminDashboardDirectoryTierFilter; label: string}> = [
    {value: 'all', label: 'All'},
    {value: 'critical', label: 'Critical'},
    {value: 'warning', label: 'Warning'},
    {value: 'watch', label: 'Watch'},
    {value: 'healthy', label: 'Healthy'},
    {value: 'unscored', label: 'Unscored'}
  ];
  readonly kindOptions: ReadonlyArray<{value: AdminDashboardDirectoryKind; label: string}> = [
    {value: 'all', label: 'All'},
    {value: 'clients', label: 'Clients'},
    {value: 'nonclients', label: 'Not in Client Manager'}
  ];
  readonly statusOptions: ReadonlyArray<{value: AdminDashboardDirectoryStatus; label: string}> = [
    {value: 'active', label: 'Active'},
    {value: 'inactive', label: 'Inactive'},
    {value: 'all', label: 'All'}
  ];

  /** One filter changed: ask again from page 1; picking the value already on asks nothing (ITP-E-304-05). */
  setFilter<K extends 'tier' | 'kind' | 'flagged' | 'status'>(key: K, value: AdminDashboardDirectoryParams[K]): void {
    if (this.params[key] === value) {
      return;
    }
    this.params = {...this.params, [key]: value, page: 1};
    this.load();
  }

  /**
   * Sorts by a column (ITP-E-304-06, Plan ITP-P-68 D-D3): the sorted column flips its direction; another starts in its
   * own (`adbDirectoryDefaultDir`). Always from page 1, keeping the search and the filters.
   */
  sortBy(sort: AdminDashboardDirectorySort): void {
    const dir = this.params.sort === sort
      ? (this.params.dir === 'asc' ? 'desc' : 'asc')
      : adbDirectoryDefaultDir(sort);
    this.params = {...this.params, sort, dir, page: 1};
    this.load();
  }

  /** The header's aria-sort: the sorted column says its direction, the others 'none'. */
  ariaSort(sort: AdminDashboardDirectorySort): 'ascending' | 'descending' | 'none' {
    if (this.params.sort !== sort) {
      return 'none';
    }
    return this.params.dir === 'asc' ? 'ascending' : 'descending';
  }

  /** The header's arrow: up or down on the sorted column, a faint up-down on the others. */
  sortIcon(sort: AdminDashboardDirectorySort): string {
    if (this.params.sort !== sort) {
      return 'fa-sort';
    }
    return this.params.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
  }

  /** "Showing 51–100 of 2,508" (ITP-E-304-07): the rows on screen, counted from the page the server served. */
  get rangeText(): string {
    if (!this.rows.length) {
      return '';
    }
    const first = (this.page - 1) * this.pageSize + 1;
    const last = first + this.rows.length - 1;
    const figure = (value: number): string => formatNumber(value, 'en-US', '1.0-0');
    return `Showing ${figure(first)}–${figure(last)} of ${figure(this.total)}`;
  }

  get hasPrevious(): boolean {
    return this.page > 1;
  }

  get hasNext(): boolean {
    return this.page * this.pageSize < this.total;
  }

  /** Asks for another page with the same search, filters, sort and page size (ITP-E-304-07). */
  goToPage(page: number): void {
    if (page < 1 || page === this.page) {
      return;
    }
    this.params = {...this.params, page};
    this.load();
  }

  onAfterExecuteNextCommand(nextCommandResponse: PlatformCommandResponse): void {}
  onAfterExecutePreviousCommand(previousCommandResponse: PlatformCommandResponse): void {}
  onBeforeExecuteNextCommand(): boolean { return false; }
  onBeforeExecutePreviousCommand(): boolean { return false; }
}
